import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import HeadingCp from '../components/HeadingCp';
import ShortDateFormat from '../components/ShortDateFormat';
import LoadingSpinner from '../components/LoadingSpinner';

const TechOperationDetails = () => {
  const { id } = useParams(); // operation_scheduler.id
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(false);
  const [op, setOp] = useState(null);      // the operation schedule row
  const [techNotes, setTechNotes] = useState('');
  const [isReady, setIsReady] = useState(false); // show "Mark Completed" once notes exist

  useEffect(() => {
    fetchOperation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOperation = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiUrl}/operation_crud.php`, {
        params: { getOperationTechDetail: id }
      });
      // expect: ['success', row] or {success:true, data: row}
      let row = null;
      if (Array.isArray(data) && data[0] === 'success') row = data[1] || null;
      else if (data?.success) row = data.data || null;

      if (!row) {
        toast.error('Operation not found', { position: 'top-center' });
        return;
      }
      setOp(row);
      const existing = row.tech_notes || '';
      setTechNotes(existing);
      setIsReady(!!existing);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.', { position: 'top-center' });
        navigate('/login');
      } else if (err.response?.status === 403) {
        toast.error("You don't have access to this area.", { position: 'top-center' });
      } else {
        toast.error('Error loading schedule', { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    setLoading(true);
    try {
      const body = { status: 'opAddTechNotes', data: { id, tech_notes: techNotes } };
      const { data } = await axios.post(`${apiUrl}/operation_crud.php`, body, {
        headers: { 'Content-Type': 'application/json' }
      });
      const ok = (Array.isArray(data) && data[0] === 'success') || data?.success;
      if (ok) {
        setIsReady(Boolean(techNotes?.trim()));
        setOp(prev => ({ ...(prev || {}), tech_notes: techNotes }));
        toast.success('Notes saved', { position: 'top-center' });
      } else {
        toast.error(data?.error || 'Failed to save notes', { position: 'top-center' });
      }
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else toast.error('Error saving notes', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const body = { status: 'opCompleted', data: { id } };
      const { data } = await axios.post(`${apiUrl}/operation_crud.php`, body, {
        headers: { 'Content-Type': 'application/json' }
      });
      const ok = (Array.isArray(data) && data[0] === 'success') || data?.success;
      if (ok) {
        toast.success('Marked as Completed', { position: 'top-center' });
        navigate('/t/t_op_list'); // ensure route is correct
      } else {
        toast.error(data?.error || 'Failed to complete', { position: 'top-center' });
      }
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else toast.error('Error completing schedule', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !op) {
    return (
      <div className="min-h-[40vh] grid place-items-center">
        <LoadingSpinner>Loading...</LoadingSpinner>
      </div>
    );
  }
  if (!op) return <div className="p-6 text-sm text-gray-500">No data.</div>;

  // chip helpers
  const statusChip = (status = '') => {
    const s = String(status).toLowerCase();
    if (s.includes('complete')) return 'bg-green-50 text-green-700 ring-green-600/20';
    if (s.includes('tentative')) return 'bg-amber-50 text-amber-700 ring-amber-600/20';
    return 'bg-gray-50 text-gray-700 ring-gray-300/60';
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Sticky header */}
      {/* <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md ring-1 ring-gray-200 shadow-sm">
        <div className="mx-auto max-w-md px-4 py-3">
          <div className="flex items-center justify-between">
            <HeadingCp label="Operation Details" />
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${statusChip(op.status)}`}
            >
              {op.status || '—'}
            </span>
          </div>
        </div>
      </div> */}
    <div className="px-4 pt-4">
      <h1 className="text-xl font-semibold text-gray-900">Operation Details</h1>
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">Tech view</p>
        <span className={`inline-flex items-center rounded-full 
                          px-2.5 py-0.5 text-xs font-semibold 
                          ring-1 ring-inset ${statusChip(op.status)}`}>
        {op.status || '—'}
        </span>  
      </div>    
    </div>

      {/* Content */}
      <div className="mx-auto max-w-md px-4 py-4 space-y-4">
        {/* Summary Card */}
        <div className="rounded-2xl bg-white/95 backdrop-blur shadow ring-1 ring-gray-200">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="text-sm font-semibold text-gray-900 truncate">
              {op.description || 'Operation'}
            </div>
            <div className="mt-1 text-xs text-gray-500">
              Log: {op.date || '—'} {op.time_log || ''}
            </div>
          </div>

          <div className="px-4 py-3 space-y-2">
            {/* Schedule */}
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Schedule</div>
                <div className="mt-0.5 text-sm font-medium text-gray-900">
                  {op.schedule_from ? <ShortDateFormat date={op.schedule_from} /> : '—'}
                  <span className="mx-1 text-gray-400">→</span>
                  {op.schedule_to ? <ShortDateFormat date={op.schedule_to} /> : '—'}
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Time</div>
                <div className="mt-0.5 text-sm font-medium text-gray-900">
                  {op.start_time || '—'}
                  {op.end_time ? ` – ${op.end_time}` : ''}
                </div>
              </div>
            </div>

            {/* Tech & Coordinator */}
            <div className="rounded-lg bg-gray-50 p-2">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">Technician</div>
              <div className="mt-0.5 text-sm font-medium text-gray-900">
                {op.tech_name || '—'}
              </div>
            </div>
            {op.user_comment && (
              <div className="rounded-lg bg-amber-50 ring-1 ring-amber-600/20 p-2">
                <div className="text-[11px] uppercase tracking-wide text-amber-700">Coordinator</div>
                <div className="mt-0.5 text-sm text-amber-800 whitespace-pre-wrap">
                  {op.user_comment}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notes Editor */}
        <div className="rounded-2xl bg-white/95 backdrop-blur shadow ring-1 ring-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Tech’s Notes</h2>
            {isReady && (
              <span className="text-[11px] rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700 ring-1 ring-emerald-600/20">
                Saved
              </span>
            )}
          </div>
          <textarea
            value={techNotes || ''}
            onChange={(e) => setTechNotes(e.target.value)}
            rows={5}
            className="mt-3 w-full rounded-xl border border-gray-300 bg-white p-3 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="Add your notes for this operation…"
          />
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              onClick={handleSaveNotes}
              className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving…' : 'Save Notes'}
            </button>
            {isReady && (
              <button
                onClick={handleComplete}
                className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Working…' : 'Mark Completed'}
              </button>
            )}
          </div>
        </div>

        {/* Back / Done actions */}
        {/* <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => navigate(-1)}
            className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Back
          </button>
          <button
            onClick={fetchOperation}
            className="rounded-xl bg-gray-800 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-600"
          >
            Refresh
          </button>
        </div> */}
        {/* Sticky footer actions */}
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur">
          <div className="mx-auto w-full max-w-md px-4 py-3">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={()=> navigate('/t/t_op_list')}
                className="w-1/2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Operation Queue
              </button>

              <button
                type="button"
                onClick={()=> navigate('/t/tech_schedule')}
                className="w-1/2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Tech Schedules
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechOperationDetails;
